import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";
import { useStore, formatPrice } from "../context/StoreContext";

const CartPage = () => {
  const { cart, updateQty, removeLine, currency, cartSubtotalInr, cartCount } = useStore();
  const [promo, setPromo] = useState("");

  if (cart.length === 0) {
    return (
      <div
        data-testid="page-cart-empty"
        className="pt-[140px] pb-20 text-center min-h-[60vh] px-4"
      >
        <h1 className="font-display uppercase text-5xl sm:text-7xl">No Damage Yet.</h1>
        <Link to="/shop" className="inline-block mt-8 btn-solid" data-testid="cart-empty-shop">
          Go break something
        </Link>
      </div>
    );
  }

  const applyPromo = () => {
    if (!promo.trim()) return;
    toast("CODE INVALID OR EXPIRED");
  };

  return (
    <div data-testid="page-cart" className="pt-[100px] pb-16">
      <div className="max-w-[1440px] mx-auto px-4 md:px-12">
        <div className="flex items-baseline gap-4 mb-10">
          <h1 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl leading-none">
            Your cart
          </h1>
          <span className="font-mono text-xs uppercase tracking-widest text-ink/60">
            {String(cartCount).padStart(2, "0")} ITEMS
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 lg:gap-16">
          {/* Lines */}
          <div className="space-y-6">
            {cart.map((line) => (
              <div
                key={`${line.id}-${line.size}`}
                data-testid={`cart-line-${line.id}-${line.size}`}
                className="grid grid-cols-[80px_1fr_auto] gap-4 border-b border-ink/12 pb-6"
              >
                <Link to={`/product/${line.id}`} className="block">
                  <img
                    src={line.image}
                    alt={line.name}
                    className="w-20 h-24 object-cover rounded-xl"
                  />
                </Link>
                <div className="min-w-0">
                  <Link
                    to={`/product/${line.id}`}
                    className="block font-body text-lg md:text-xl hover:text-dc-red transition-colors leading-tight"
                  >
                    {line.name}
                  </Link>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mt-1">
                    SIZE {line.size}
                  </p>
                  <p className="font-mono text-sm mt-2">
                    {formatPrice(line.priceInr * line.quantity, currency)}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => updateQty(line.id, line.size, line.quantity - 1)}
                      aria-label="Decrease"
                      data-testid={`cart-dec-${line.id}-${line.size}`}
                      className="w-8 h-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-cream transition-colors"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="font-mono text-sm w-8 text-center">{line.quantity}</span>
                    <button
                      onClick={() => updateQty(line.id, line.size, line.quantity + 1)}
                      aria-label="Increase"
                      data-testid={`cart-inc-${line.id}-${line.size}`}
                      className="w-8 h-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-cream transition-colors"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => removeLine(line.id, line.size)}
                  aria-label="Remove"
                  data-testid={`cart-remove-${line.id}-${line.size}`}
                  className="self-start p-2 hover:text-dc-red"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <aside className="card-surface rounded-[20px] shadow-card p-6 md:p-8 h-fit">
            <h2 className="font-display uppercase text-2xl mb-5">Summary</h2>
            <div className="font-mono text-sm space-y-3">
              <div className="flex justify-between">
                <span className="text-ink/70 uppercase text-xs tracking-widest">Subtotal</span>
                <span data-testid="cart-subtotal">{formatPrice(cartSubtotalInr, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink/70 uppercase text-xs tracking-widest">Shipping</span>
                <span className="text-ink/70 text-xs">CALCULATED AT CHECKOUT</span>
              </div>
              <div className="h-px bg-ink/30 my-3" />
              <div className="flex justify-between font-display uppercase text-xl">
                <span>Total</span>
                <span data-testid="cart-total">{formatPrice(cartSubtotalInr, currency)}</span>
              </div>
            </div>

            <div className="mt-6 flex rounded-full border border-ink/15 overflow-hidden">
              <input
                type="text"
                value={promo}
                onChange={(e) => setPromo(e.target.value)}
                placeholder="PROMO CODE"
                data-testid="cart-promo-input"
                className="flex-1 bg-transparent font-mono text-xs uppercase tracking-widest px-3 py-2 outline-none"
              />
              <button
                onClick={applyPromo}
                data-testid="cart-promo-apply"
                className="bg-ink text-cream font-mono text-xs uppercase tracking-widest px-4 hover:bg-dc-red transition-colors"
              >
                Apply
              </button>
            </div>

            <Link
              to="/checkout"
              data-testid="cart-checkout"
              className="block text-center mt-6 btn-solid"
            >
              Checkout &rarr;
            </Link>
            <Link
              to="/shop"
              className="block text-center mt-3 font-mono text-xs uppercase tracking-widest text-ink/70 hover:text-ink"
              data-testid="cart-continue"
            >
              Continue shopping
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
